import React from 'react';
import {Helmet} from "react-helmet-async";
import {
  Switch,
} from "react-router-dom";
import { renderRoutes } from 'react-router-config';
import styled, {createGlobalStyle} from 'styled-components'
import { CookiesProvider } from 'react-cookie'
import { ModalProvider } from 'styled-react-modal'
import { IntlProvider, FormattedMessage, useIntl } from "react-intl"
import { DFPSlotsProvider } from 'react-dfp';
import { ToastContainer, Bounce } from 'react-toastify';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import {Wrapper, 
  Container, Row, Col} from './StyledComponents'
import GaTracker from './components/GaTracker';
import messages from "./common/lang/messages";
import {LoggedInContextProvider} from './auth/AuthContext'
import {TenancyContext} from './client/useTenant'
import {LangContext} from './useLang'
import TENANT_TO_LANG from './common/TENANT_TO_LANG'
import {Header} from './Header';
import Routes from './routes';
import ForgotPasswordModal from './components/ForgotPasswordModal';

const Background = styled.div`
  overflow: scroll;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  min-width: 100vw;
  min-height: 100vh;
  width: fit-content;
  height: 100vh;
  z-index: 30;
  background-color: rgba(0,0,0,0.5);
`

const Footer = styled.div`
  width: 100%;
  padding: 32px 0 16px 0;
  margin: 0 auto;
  background: linear-gradient(90deg, #f8fafc 0%, #e2e8f0 100%);
  box-shadow: 0 -2px 16px 0 rgba(0,0,0,0.04);
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    font-size: 0.9rem;
    font-family: "Roboto";
  }
  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }
`
const Body = styled(Container)`
  margin: 0;
  @media (min-width: 576px) {
    max-width: none;
  }
`

const FooterLink = styled.a`
  color: #64748b;
  font-size: 14px;
  margin: 0 10px;
  text-decoration: none;
  transition: color 0.2s;
  border-radius: 4px;
  padding: 2px 8px;
  &:hover {
    color: #0ea5e9;
    background: #f1f5f9;
    text-decoration: underline;
  }
`;

const FooterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 8px;
`;

const FooterEmail = styled.p`
  color: #334155;
  font-size: 15px;
  margin-bottom: 12px;
  text-align: center;
`;

function FooterComponent() {
  const intl = useIntl();
  
  return (
    <Footer role="banner">
      <FooterEmail>
        <FormattedMessage id="footer.emailCta"/> &nbsp;
        <a href={`mailto:${intl.formatMessage({id: "footer.email"})}`} 
          style={{color: "#0ea5e9", textDecoration: "none", fontWeight: 500}}>
          <FormattedMessage id="footer.email" />
        </a>
      </FooterEmail>
      <FooterRow>
        <FooterLink href={"https://www.historyworm.com"}>History Worm</FooterLink>
        <FooterLink href={"https://www.fiyatarsivi.com"}>Fiyat Arşivi</FooterLink>
        <FooterLink href={"https://www.nedemek.page"}>Ne Demek</FooterLink>
        <FooterLink href={"https://www.wikiwhat.page"}>Wiki What</FooterLink>
        <FooterLink href={"https://www.pricearchive.page"}>Price Archive</FooterLink>
      </FooterRow>
      <FooterRow>
        <FooterLink href={"https://de.wikiwhat.page"}>Wiki What De</FooterLink>
        <FooterLink href={"https://pl.wikiwhat.page"}>Wiki What Pl</FooterLink>
        <FooterLink href={"https://es.wikiwhat.page"}>Wiki What Es</FooterLink>
        <FooterLink href={"https://fr.wikiwhat.page"}>Wiki What Fr</FooterLink>
        <FooterLink href={"https://th.wikiwhat.page"}>Wiki What Th</FooterLink>
      </FooterRow>
      <div style={{color: '#94a3b8', fontSize: 12, marginTop: 10, textAlign: 'center'}}>
        © {new Date().getFullYear()} Gaste Arşivi
      </div>
    </Footer>
  );
}

function App(props) {
  const {tenant, lang} = props
  const [showForgot, setShowForgot] = React.useState(false);

  console.debug("[app] Gaste Arşivi render app lang:", tenant)
  
  return (
    <TenancyContext.Provider value={tenant} >
    <LangContext.Provider value={{lang: TENANT_TO_LANG[tenant]}}>
    <CookiesProvider>
    <IntlProvider locale={lang} defaultLocale="tr" messages={messages[lang]}>
    <LoggedInContextProvider>
      <GaTracker>
        <DFPSlotsProvider dfpNetworkId="22024215299">
          <GlobalStyle />
          <Helmet 
            defer={false} 
            htmlAttributes={{lang}}
          >
            <link rel="icon" href={`/static/favicon.png`} />
            //manifest.json provides metadata used when your web app is installed on a
            //user's mobile device or desktop. See https://developers.google.com/web/fundamentals/web-app-manifest/
            <link rel="manifest" href={`/static/manifest.json`} />
            <meta property="fb:app_id" content="1665553766959662" />
            <link title="Gaste Arşivi" rel="search" type="application/opensearchdescription+xml" href="/odesc.xml" />
          </Helmet>
          <Wrapper>
          <ToastContainer position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick={false}
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            transition={Bounce}
          />
          <ModalProvider backgroundComponent={Background}>
            <Header />
            <ForgotPasswordModal isOpen={showForgot} onClose={() => setShowForgot(false)} />
            <Body>
              {/* A <Switch> looks through its children <Route>s and
                  renders the first one that matches the current URL. */}
              <Switch>
                {renderRoutes(Routes)}
              </Switch>
            </Body>
            <FooterComponent />
          </ModalProvider>
          </Wrapper>
        </DFPSlotsProvider>
      </GaTracker>
    </LoggedInContextProvider>
    </IntlProvider>
    </CookiesProvider>
    </LangContext.Provider>
    </TenancyContext.Provider>
  );
}

export default App;
